import { useState, useMemo } from 'react';
import {
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';
import { ProcessTypeConfig } from '@/shared/context/ProcessSettingsProvider';

interface EditingState {
  type: string;
  displayName: string;
  color: string;
}

export default function ProcessSettings() {
  const { settings, isLoading, updateProcessType, deleteProcessType } =
    useProcessSettings();
  const [editingType, setEditingType] = useState<string | null>(null);
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newType, setNewType] = useState<EditingState>({
    type: '',
    displayName: '',
    color: '#6B7280',
  });

  const processTypesList = useMemo(() => {
    if (!settings?.processTypes) return [];
    return Object.values(settings.processTypes).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }, [settings]);

  const handleStartEdit = (config: ProcessTypeConfig) => {
    setEditingType(config.type);
    setEditingState({
      type: config.type,
      displayName: config.displayName,
      color: config.color,
    });
  };

  const handleCancelEdit = () => {
    setEditingType(null);
    setEditingState(null);
  };

  const handleSaveEdit = async () => {
    if (!editingState) return;

    await updateProcessType(editingState.type, {
      displayName: editingState.displayName,
      color: editingState.color,
    });

    setEditingType(null);
    setEditingState(null);
  };

  const handleDelete = async (type: string) => {
    if (window.confirm(`Are you sure you want to delete "${type}"?`)) {
      await deleteProcessType(type);
    }
  };

  const handleAddNew = async () => {
    if (!newType.type.trim() || !newType.displayName.trim()) {
      alert('Please enter both process type and display name');
      return;
    }

    await updateProcessType(newType.type, {
      displayName: newType.displayName,
      color: newType.color,
    });

    setIsAddingNew(false);
    setNewType({ type: '', displayName: '', color: '#6B7280' });
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewType({ type: '', displayName: '', color: '#6B7280' });
  };

  if (isLoading) {
    return (
      <Stack
        className="h-full w-full items-center justify-center p-24"
        gap="16px"
      >
        <CircularProgress />
        <Typography variant="body1" className="text-stone-300">
          Loading process settings...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap="24px" className="w-full p-24 max-w-[1200px] mx-auto">
      {/* Header */}
      <Stack gap="8px">
        <Typography variant="h3" className="text-stone-200">
          Process Settings
        </Typography>
        <Typography variant="body1" className="text-stone-300">
          Customize the display names and colors for process types across your
          portfolio
        </Typography>
      </Stack>

      {/* Add New Button */}
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddingNew(true)}
          disabled={isAddingNew}
        >
          Add Process Type
        </Button>
      </Stack>

      {/* Add New Process Form */}
      {isAddingNew && (
        <Card className="bg-zinc-900/50 p-16 border border-stone-200/20">
          <Stack gap="16px">
            <Typography variant="h6" className="text-stone-200">
              New Process Type
            </Typography>
            <Stack direction="row" gap="16px" alignItems="flex-start">
              <TextField
                label="Process Type Key"
                value={newType.type}
                onChange={(e) =>
                  setNewType({ ...newType, type: e.target.value })
                }
                placeholder="e.g., Move In, Make Ready"
                className="flex-1"
                size="small"
                helperText="Internal identifier (cannot be changed later)"
              />
              <TextField
                label="Display Name"
                value={newType.displayName}
                onChange={(e) =>
                  setNewType({ ...newType, displayName: e.target.value })
                }
                placeholder="e.g., Move In Process"
                className="flex-1"
                size="small"
                helperText="Name shown in the UI"
              />
              <Stack gap="4px">
                <Typography variant="caption" className="text-stone-300 px-1">
                  Color
                </Typography>
                <input
                  type="color"
                  value={newType.color}
                  onChange={(e) =>
                    setNewType({ ...newType, color: e.target.value })
                  }
                  className="h-40 w-80 cursor-pointer rounded border border-stone-200/20"
                />
              </Stack>
              <Stack direction="row" gap="8px" className="pt-20">
                <Tooltip title="Save">
                  <IconButton
                    onClick={handleAddNew}
                    color="primary"
                    size="small"
                  >
                    <SaveIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    onClick={handleCancelAdd}
                    color="default"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Process Types List */}
      <Stack gap="12px">
        {processTypesList.length === 0 ? (
          <Card className="bg-zinc-900/30 p-24 border border-stone-200/20">
            <Typography variant="body1" className="text-stone-300 text-center">
              No process types configured yet. Add one to get started.
            </Typography>
          </Card>
        ) : (
          processTypesList.map((config) => (
            <Card
              key={config.type}
              className="bg-zinc-900/30 p-16 border border-stone-200/10 hover:border-stone-200/30 transition-colors"
            >
              {editingType === config.type && editingState ? (
                // Edit Mode
                <Stack gap="16px">
                  <Stack direction="row" gap="16px" alignItems="flex-start">
                    <Box
                      className="h-40 w-40 rounded shadow-sm"
                      style={{ backgroundColor: editingState.color }}
                    />
                    <Stack gap="8px" className="flex-1">
                      <Typography variant="caption" className="text-stone-500">
                        {config.type}
                      </Typography>
                      <TextField
                        label="Display Name"
                        value={editingState.displayName}
                        onChange={(e) =>
                          setEditingState({
                            ...editingState,
                            displayName: e.target.value,
                          })
                        }
                        size="small"
                        fullWidth
                      />
                    </Stack>
                    <Stack gap="4px">
                      <Typography
                        variant="caption"
                        className="text-stone-300 px-1"
                      >
                        Color
                      </Typography>
                      <input
                        type="color"
                        value={editingState.color}
                        onChange={(e) =>
                          setEditingState({
                            ...editingState,
                            color: e.target.value,
                          })
                        }
                        className="h-40 w-80 cursor-pointer rounded border border-stone-200/20"
                      />
                    </Stack>
                    <Stack direction="row" gap="8px" className="pt-20">
                      <Tooltip title="Save">
                        <IconButton
                          onClick={handleSaveEdit}
                          color="primary"
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          onClick={handleCancelEdit}
                          color="default"
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                // View Mode
                <Stack
                  direction="row"
                  gap="16px"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" gap="16px" alignItems="center">
                    <Box
                      className="h-40 w-40 rounded shadow-sm"
                      style={{ backgroundColor: config.color }}
                    />
                    <Stack gap="4px">
                      <Typography variant="h6" className="text-stone-200">
                        {config.displayName}
                      </Typography>
                      <Typography variant="caption" className="text-stone-500">
                        Type: {config.type}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap="8px">
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleStartEdit(config)}
                        size="small"
                        className="text-stone-300 hover:text-blue-400"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => handleDelete(config.type)}
                        size="small"
                        className="text-stone-300 hover:text-red-400"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              )}
            </Card>
          ))
        )}
      </Stack>

      {/* Info Box */}
      <Card className="bg-blue-500/10 p-16 border border-blue-500/20">
        <Stack gap="8px">
          <Typography variant="body2" className="text-blue-300 font-semibold">
            💡 How it works
          </Typography>
          <Typography variant="caption" className="text-blue-200/80">
            • Display names are shown throughout the app instead of internal
            process type keys
          </Typography>
          <Typography variant="caption" className="text-blue-200/80">
            • Colors are used in charts, cards, and process indicators
          </Typography>
          <Typography variant="caption" className="text-blue-200/80">
            • Changes apply immediately across all projects in your organization
          </Typography>
          <Typography variant="caption" className="text-blue-200/80">
            • Process types are shared across your entire organization
          </Typography>
        </Stack>
      </Card>
    </Stack>
  );
}
